// PRE RMA — Bons de commande (FANOME, Paludisme, Planification familiale).
//
// SOURCE DES FORMULES : « Bon de commande.xlsx » (feuille Bon_de_commande), colonnes A..P, MODIFIÉE par le client pour la période de 3 mois :
//   D  Quantités disponibles au début de la période
//   E  Quantités reçues au cours de la période
//   F  Quantités distribuées aux clients au cours de la période (somme des 3 mois)
//   G  Quantités perdues au cours de la période (pertes)
//   H  Quantités transférées au cours de la période (ajustement)
//   I  Quantités restantes en fin de période         I = (D+E)-F-G-H
//   J  Nombre de jours de rupture de stock (sur la période)
//   K  CMM ajustée de la période                     K = (F*30)/(90-J)      ← formule demandée par le client (3 mois = 90 jours)
//   L  Stock maximum                                 L = K*4
//   M  Quantité à commander                          M = L-I
//   N  Quantités livrées      O  Prix unitaire       P = N*O
//
// FENÊTRE DE 3 MOIS : « commande d'octobre = août, septembre, octobre » (fenêtre glissante).
// D = début du 1er mois de la fenêtre ; E, F, G, H, J = sommes sur la fenêtre ; I = formule ci-dessus.
// La CMM est une consommation MENSUELLE : F (3 mois) × 30 jours ÷ nombre de jours où le produit était disponible (90 − J).
// Si la fenêtre compte n mois, le diviseur devient 30×n − J (n = 3 ⇒ 90 − J, formule du client).
//
// RIEN n'est inventé : unité de conversion inconnue → quantité NON calculée et ligne marquée bloquante ;
// donnée absente → avertissement explicite (jamais un 0 silencieux).

export const FENETRE_MOIS_DEFAUT = 3;
export const FORMULE_CMM_FENETRE = 'K=(F*30)/(90-J)';
export const JOURS_MOIS_FORMULE = 30; // « 30 » du numérateur : convertit la consommation de la période en consommation mensuelle
/** Nombre de jours de la période : 30 × nombre de mois (90 pour 3 mois). */
export const joursPeriode = (nbMois: number): number => JOURS_MOIS_FORMULE * nbMois;
export const MULTIPLICATEUR_STOCK_MAX = 4; // « L = K*4 »

export interface MoisRef { annee: number; mois: number }
export const NOMS_MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
export const libelleMois = (m: MoisRef): string => `${NOMS_MOIS[m.mois - 1]} ${m.annee}`;

/** Mois de la fenêtre, du plus ancien au plus récent, terminant au mois de commande (franchit le changement d'année). */
export function fenetreMois(annee: number, mois: number, n: number = FENETRE_MOIS_DEFAUT): MoisRef[] {
  if (!Number.isInteger(annee) || annee < 2000 || annee > 2100) throw new Error(`Année invalide : ${annee}`);
  if (!Number.isInteger(mois) || mois < 1 || mois > 12) throw new Error(`Mois invalide : ${mois}`);
  if (!Number.isInteger(n) || n < 1 || n > 12) throw new Error(`Taille de fenêtre invalide : ${n}`);
  const idx = annee * 12 + (mois - 1);
  return Array.from({ length: n }, (_, i) => { const k = idx - (n - 1) + i; return { annee: Math.floor(k / 12), mois: (k % 12) + 1 }; });
}

export interface MoisValeurs {
  annee: number; mois: number;
  /** false = aucune donnée enregistrée ce mois pour ce produit (≠ 0). */
  present: boolean;
  debut: number | null; recue: number | null; utilisee: number | null; perdue: number | null; ajustement: number | null;
  /** Stock de fin de mois tel qu'enregistré (pour contrôler la continuité et les divergences). */
  fin: number | null;
  joursRupture: number | null;
}
export const moisVide = (m: MoisRef): MoisValeurs => ({ ...m, present: false, debut: null, recue: null, utilisee: null, perdue: null, ajustement: null, fin: null, joursRupture: null });

const r4 = (x: number): number => Math.round(x * 10_000) / 10_000;
const z = (x: number | null | undefined): number => (typeof x === 'number' && Number.isFinite(x) ? x : 0);

export interface EntreeLigneCommande {
  libelle: string;
  unite: string | null;
  /** Une valeur par mois de la fenêtre, dans l'ordre chronologique. */
  mois: MoisValeurs[];
  /**
   * Remplace « quantités distribuées » (F) mois par mois (ex. ACT : cas TDR+ traités par ACT, issus des consultations).
   * Les quantités du module Intrants restent utilisées pour le contrôle de divergence.
   */
  distribueesParMois?: Array<number | null>;
  /** true quand les quantités doivent être converties d'une unité de stock vers l'unité de commande mais que le facteur est inconnu. */
  conversionManquante?: boolean;
  /** Jours de rupture saisis à la main pour la période (remplace ceux des mois). */
  joursRuptureManuel?: number | null;
  prixUnitaire: number | null;
  livrees: number | null;
  observations?: string;
}

export interface LigneCommande {
  libelle: string; unite: string | null;
  D: number | null; E: number | null; F: number | null; G: number | null; H: number | null;
  I: number | null; J: number | null; K: number | null; L: number | null; M: number | null;
  N: number | null; O: number | null; P: number | null;
  /** Valeurs « fin de mois » enregistrées, pour comparaison avec I. */
  finEnregistree: number | null;
  /** Quantité distribuée (F) et jours de rupture (J) de chaque mois de la fenêtre (null = mois sans donnée) : détail affiché dans l'Excel. */
  fParMois: Array<number | null>;
  jParMois: Array<number | null>;
  observations: string;
  avertissements: string[];
  /** true = la ligne ne peut pas servir à commander tant que le problème n'est pas résolu. */
  bloquant: boolean;
  /** true = tous les mois de la fenêtre ont des données. */
  complete: boolean;
}

export function calculerLigneCommande(e: EntreeLigneCommande): LigneCommande {
  const av: string[] = [];
  const base: LigneCommande = {
    libelle: e.libelle, unite: e.unite,
    D: null, E: null, F: null, G: null, H: null, I: null, J: null, K: null, L: null, M: null,
    N: e.livrees, O: e.prixUnitaire, P: null, finEnregistree: null,
    fParMois: e.mois.map(() => null), jParMois: e.mois.map(() => null),
    observations: e.observations ?? '', avertissements: av, bloquant: false, complete: false,
  };
  if (e.unite === null || e.unite.trim() === '') av.push('Unité de commande non renseignée : à compléter dans la liste des produits.');
  if (e.conversionManquante) {
    av.push('Facteur de conversion vers l\'unité de commande non défini : quantités NON calculées (ne jamais additionner des unités différentes).');
    return { ...base, bloquant: true };
  }
  const presents = e.mois.filter((m) => m.present);
  if (presents.length === 0) {
    av.push('Aucune donnée enregistrée sur la période : quantités non calculées.');
    return { ...base, bloquant: true };
  }
  base.complete = presents.length === e.mois.length;
  if (!base.complete) {
    const manquants = e.mois.filter((m) => !m.present).map((m) => libelleMois(m)).join(', ');
    av.push(`Données absentes pour : ${manquants}. La période est incomplète ; le calcul porte sur les mois renseignés.`);
  }
  if (e.distribueesParMois && e.distribueesParMois.length !== e.mois.length) throw new Error('distribueesParMois doit avoir la même longueur que mois');

  const moisUtiles = e.mois.map((m, i) => ({ m, i, F: e.distribueesParMois ? e.distribueesParMois[i] : m.utilisee })).filter((x) => x.m.present);
  const premier = moisUtiles[0].m;
  if (premier !== e.mois[0]) av.push(`Le premier mois de la fenêtre n'a pas de données : le stock initial est celui de ${libelleMois(premier)}.`);
  if (premier.debut === null) av.push(`Stock initial (début de ${libelleMois(premier)}) non renseigné : considéré comme 0.`);

  const D = z(premier.debut);
  const E = moisUtiles.reduce((a, x) => a + z(x.m.recue), 0);
  const F = moisUtiles.reduce((a, x) => a + z(x.F), 0);
  const G = moisUtiles.reduce((a, x) => a + z(x.m.perdue), 0);
  const H = moisUtiles.reduce((a, x) => a + z(x.m.ajustement), 0);
  for (const x of moisUtiles) if (x.F === null) av.push(`Quantité distribuée non renseignée en ${libelleMois(x.m)} : considérée comme 0.`);
  const I = r4((D + E) - F - G - H);

  // Jours de rupture sur la période (somme des mois, ou saisie manuelle de la période)
  let J: number;
  const jrMois = moisUtiles.map((x) => x.m.joursRupture);
  if (e.joursRuptureManuel !== undefined && e.joursRuptureManuel !== null) J = e.joursRuptureManuel;
  else {
    if (jrMois.some((v) => v === null)) av.push('Jours de rupture de stock non renseignés pour certains mois : considérés comme 0 (à saisir si le produit a été en rupture).');
    J = jrMois.reduce<number>((a, v) => a + z(v), 0);
  }
  if (J < 0 || !Number.isFinite(J)) { av.push('Nombre de jours de rupture invalide.'); return { ...base, bloquant: true }; }

  // CMM ajustée : K = (F*30)/(90-J). Diviseur = jours de la période − jours de rupture : contrôlé (jamais de division par 0 ni négative).
  const jp = joursPeriode(e.mois.length);
  const diviseur = jp - J;
  let Kexact: number | null = null;
  if (diviseur <= 0) av.push(`${J} jours de rupture sur ${jp} jours de période : CMM non calculable (diviseur ${jp} − J ≤ 0).`);
  else Kexact = (F * JOURS_MOIS_FORMULE) / diviseur;
  if (J > jp) av.push(`Jours de rupture (${J}) supérieurs à la durée de la période (${jp} jours) : à corriger.`);
  // L et M sont calculés sur K NON arrondi (comme les cellules Excel) ; l'arrondi à 4 décimales n'intervient qu'à l'affichage du résultat.
  const K = Kexact === null ? null : r4(Kexact);
  const L = Kexact === null ? null : r4(Kexact * MULTIPLICATEUR_STOCK_MAX);
  const M = Kexact === null ? null : r4(Kexact * MULTIPLICATEUR_STOCK_MAX - I);

  // Contrôles
  if (I < 0) { av.push(`Quantités restantes négatives (${I}) : sorties supérieures au stock disponible.`); base.bloquant = true; }
  if (M !== null && M < 0) av.push(`Quantité à commander négative (${M}) : le stock restant dépasse le stock maximum, aucune commande nécessaire.`);
  // cohérence de chaque mois : stock initial + reçu − utilisé − perdu − ajusté doit égaler le stock final enregistré (jamais corrigé en silence)
  for (const m of e.mois) {
    if (!m.present || m.debut === null || m.fin === null) continue;
    const attendu = r4(m.debut + z(m.recue) - z(m.utilisee) - z(m.perdue) - z(m.ajustement));
    if (attendu !== r4(m.fin)) {
      av.push(`Incohérence en ${libelleMois(m)} : stock initial ${m.debut} + reçu ${z(m.recue)} − utilisé ${z(m.utilisee)} − perdu ${z(m.perdue)} − ajusté ${z(m.ajustement)} = ${attendu}, mais le stock final enregistré est ${m.fin} (écart ${r4(m.fin - attendu)}). Données non modifiées : à vérifier.`);
    }
  }
  // continuité des stocks d'un mois au suivant
  for (let i = 1; i < e.mois.length; i++) {
    const a = e.mois[i - 1], b = e.mois[i];
    if (a.present && b.present && a.fin !== null && b.debut !== null && r4(a.fin) !== r4(b.debut)) {
      av.push(`Continuité rompue : stock final de ${libelleMois(a)} = ${a.fin} mais stock initial de ${libelleMois(b)} = ${b.debut}.`);
    }
  }
  const dernier = moisUtiles[moisUtiles.length - 1].m;
  base.finEnregistree = dernier.fin;
  if (dernier.fin !== null && r4(dernier.fin) !== I) av.push(`Divergence de stock : restant calculé (I = ${I}) ≠ stock de fin enregistré de ${libelleMois(dernier)} (${dernier.fin}).`);
  if (e.distribueesParMois) {
    const sIntrants = moisUtiles.reduce((a, x) => a + z(x.m.utilisee), 0);
    if (r4(sIntrants) !== r4(F)) av.push(`Quantité distribuée (F = ${r4(F)}, issue des consultations) ≠ quantité utilisée dans le module Intrants (${r4(sIntrants)}) : à rapprocher.`);
  }

  const P = e.livrees !== null && e.prixUnitaire !== null ? r4(e.livrees * e.prixUnitaire) : null;
  if (e.prixUnitaire === null) av.push('Prix unitaire non renseigné : montant non calculé.');
  const fParMois = e.mois.map<number | null>(() => null), jParMois = e.mois.map<number | null>(() => null);
  moisUtiles.forEach((x, k) => { fParMois[x.i] = r4(z(x.F)); jParMois[x.i] = e.joursRuptureManuel != null ? null : jrMois[k]; });
  return { ...base, D, E: r4(E), F: r4(F), G: r4(G), H: r4(H), I, J, K, L, M, P, fParMois, jParMois, observations: e.observations ?? '' };
}

export interface TotauxCommande { nbLignes: number; nbBloquantes: number; nbAvertissements: number; montantTotal: number; lignesSansPrix: number }
export function totauxCommande(lignes: readonly LigneCommande[]): TotauxCommande {
  return {
    nbLignes: lignes.length,
    nbBloquantes: lignes.filter((l) => l.bloquant).length,
    nbAvertissements: lignes.reduce((a, l) => a + l.avertissements.length, 0),
    montantTotal: r4(lignes.reduce((a, l) => a + (l.P ?? 0), 0)),
    lignesSansPrix: lignes.filter((l) => l.P === null).length,
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Catalogue des produits (liste préétablie, modifiable) — Paludisme et Planification familiale
// ---------------------------------------------------------------------------------------------------------------
export type ProgrammeCommande = 'FANOME' | 'PALU' | 'PF';
export const PROGRAMMES_COMMANDE: readonly ProgrammeCommande[] = ['FANOME', 'PALU', 'PF'];

export interface SourceProduit {
  /** Nom du produit dans le module Intrants (STOCK_PROGRAMMES). */
  nom: string;
  /** Unité telle qu'elle est tenue dans le module Intrants. */
  uniteSource: string | null;
  /** Quantité d'unité de commande pour 1 unité source ; null = INCONNU (bloque le calcul). */
  facteur: number | null;
}

/** Tranches d'ACT issues des consultations (calculerReferencesPalu). */
export type TrancheAct = 'act0_11mois' | 'act1_5ans' | 'act6_13ans' | 'act14ansEtPlus';

export interface ProduitCatalogue {
  cle: string;
  programme: ProgrammeCommande;
  libelle: string;
  /** null = unité non documentée dans les fichiers fournis (à renseigner ; jamais inventée). */
  unite: string | null;
  categorie: string;
  ordre: number;
  actif: boolean;
  sources: SourceProduit[];
  sourceUtilisee: 'INTRANTS' | 'CONSULTATIONS_ACT';
  trancheAct?: TrancheAct;
  regleCalcul: string;
  /** false = regroupement / conversion proposé mais à faire confirmer par le programme. */
  conversionConfirmee: boolean;
  note?: string;
}

const REGLE_STANDARD = 'Bon de commande : I=(D+E)-F-G-H ; K=(F×30)/(90−J) (période de 3 mois) ; L=K×4 ; M=L−I ; P=N×O.';
const REGLE_ACT = `${REGLE_STANDARD} F = cas avec TDR positif traités par ACT (consultations) pour la tranche d'âge ; D, E, G, H = somme des présentations ASAQ et AL regroupées.`;
const SRC = (nom: string, uniteSource: string | null, facteur: number | null): SourceProduit => ({ nom, uniteSource, facteur });

/** Liste initiale du programme Paludisme : les 7 lignes demandées, dans l'ordre demandé, puis les produits du module Intrants non encore confirmés (inactifs). */
export const PRODUITS_PALU_DEFAUT: ProduitCatalogue[] = [
  { cle: 'PALU-ACT-2-11M', programme: 'PALU', libelle: 'ACT 2–11 mois', unite: 'dose', categorie: 'ACT', ordre: 1, actif: true, sourceUtilisee: 'CONSULTATIONS_ACT', trancheAct: 'act0_11mois', regleCalcul: REGLE_ACT, conversionConfirmee: false,
    sources: [SRC('ASAQ 2-11 mois', 'dose', 1), SRC('AL 5-14 kg', 'dose', 1)], note: 'Regroupement ASAQ 2–11 mois + AL 5–14 kg (le client écrit « 5–15 kg » ; le module Intrants porte « 5-14 kg »). Les consultations comptent 0–11 mois.' },
  { cle: 'PALU-ACT-1-5A', programme: 'PALU', libelle: 'ACT 1–5 ans', unite: 'dose', categorie: 'ACT', ordre: 2, actif: true, sourceUtilisee: 'CONSULTATIONS_ACT', trancheAct: 'act1_5ans', regleCalcul: REGLE_ACT, conversionConfirmee: false,
    sources: [SRC('ASAQ 1-5 ans', 'dose', 1), SRC('AL 15-24 kg', 'dose', 1)] },
  { cle: 'PALU-ACT-6-13A', programme: 'PALU', libelle: 'ACT 6–13 ans', unite: 'dose', categorie: 'ACT', ordre: 3, actif: true, sourceUtilisee: 'CONSULTATIONS_ACT', trancheAct: 'act6_13ans', regleCalcul: REGLE_ACT, conversionConfirmee: false,
    sources: [SRC('ASAQ 6-13 ans', 'dose', 1), SRC('AL 25-34 kg', 'dose', 1)] },
  { cle: 'PALU-ACT-14A', programme: 'PALU', libelle: 'ACT 14 ans et plus', unite: 'dose', categorie: 'ACT', ordre: 4, actif: true, sourceUtilisee: 'CONSULTATIONS_ACT', trancheAct: 'act14ansEtPlus', regleCalcul: REGLE_ACT, conversionConfirmee: false,
    sources: [SRC('ASAQ 14 ans +', 'dose', 1), SRC('AL >35 kg', 'dose', 1)] },
  { cle: 'PALU-TDR', programme: 'PALU', libelle: 'Kit TDR', unite: 'unité', categorie: 'Diagnostic', ordre: 5, actif: true, sourceUtilisee: 'INTRANTS', regleCalcul: `${REGLE_STANDARD} F = quantités utilisées du module Intrants (RDT).`, conversionConfirmee: false,
    sources: [SRC('RDT', 'unité', 1)], note: 'Le module Intrants ne précise pas si « RDT » est compté en tests ou en kits : conversion 1:1 à confirmer.' },
  { cle: 'PALU-SP', programme: 'PALU', libelle: 'Sulfadoxine-Pyriméthamine (SP)', unite: 'dose', categorie: 'Chimioprévention', ordre: 6, actif: true, sourceUtilisee: 'INTRANTS', regleCalcul: `${REGLE_STANDARD} F = quantités utilisées du module Intrants, DÉJÀ enregistrées en doses (aucune conversion).`, conversionConfirmee: true,
    sources: [SRC('Sulfadoxine-Pyriméthamine femme enceinte (cp)', 'dose', 1)], note: 'Confirmé par le programme : les quantités de SP du module Intrants Palu sont déjà enregistrées en DOSES, bien que le libellé du module mentionne « (cp) ». Elles sont utilisées telles quelles : aucune conversion comprimés/doses.' },
  { cle: 'PALU-ARTESUNATE-INJ', programme: 'PALU', libelle: 'Artesunate injectable 60 mg', unite: 'fl.', categorie: 'Paludisme grave', ordre: 7, actif: true, sourceUtilisee: 'INTRANTS', regleCalcul: `${REGLE_STANDARD} F = quantités utilisées du module Intrants (flacons).`, conversionConfirmee: false,
    sources: [SRC('Artesunate injectable', 'flacon', 1)], note: 'Un flacon n\'est pas une dose administrée : la quantité est comptée en flacons.' },
  // Produits présents dans le module Intrants mais dont l'unité de commande n'est pas documentée : inactifs jusqu'à confirmation.
  { cle: 'PALU-ARTESUNATE-SUPPO', programme: 'PALU', libelle: 'Artesunate suppositoire', unite: null, categorie: 'Paludisme grave', ordre: 8, actif: false, sourceUtilisee: 'INTRANTS', regleCalcul: REGLE_STANDARD, conversionConfirmee: false,
    sources: [SRC('Artesunate suppo', null, 1)], note: 'Présent dans le module Intrants ; unité et présentation officielles non fournies : à confirmer avant activation.' },
  { cle: 'PALU-PRIMAQUINE', programme: 'PALU', libelle: 'Primaquine', unite: null, categorie: 'Paludisme', ordre: 9, actif: false, sourceUtilisee: 'INTRANTS', regleCalcul: REGLE_STANDARD, conversionConfirmee: false,
    sources: [SRC('Primaquine', null, 1)], note: 'Présent dans le module Intrants ; unité officielle non fournie : à confirmer avant activation.' },
  { cle: 'PALU-MID', programme: 'PALU', libelle: 'MID de routine', unite: null, categorie: 'Prévention', ordre: 10, actif: false, sourceUtilisee: 'INTRANTS', regleCalcul: REGLE_STANDARD, conversionConfirmee: false,
    sources: [SRC('MID de routine', null, 1)], note: 'Présent dans le module Intrants ; unité officielle non fournie : à confirmer avant activation.' },
];

/** Planification familiale : les produits du module Intrants PF, dans son ordre. Les unités ne figurent dans aucun fichier fourni → à renseigner. */
export const PRODUITS_PF_DEFAUT = (produitsModule: readonly string[]): ProduitCatalogue[] =>
  produitsModule.map((nom, i) => ({
    cle: `PF-${String(i + 1).padStart(2, '0')}`, programme: 'PF' as const, libelle: nom, unite: null, categorie: 'Planification familiale', ordre: i + 1, actif: true,
    sources: [SRC(nom, null, 1)], sourceUtilisee: 'INTRANTS' as const, regleCalcul: `${REGLE_STANDARD} F = quantités utilisées du module Intrants PF.`, conversionConfirmee: false,
    note: 'Unité de commande non documentée dans les fichiers fournis : à renseigner.',
  }));

export function validerProduitCatalogue(p: Partial<ProduitCatalogue>): { ok: boolean; erreurs: string[] } {
  const e: string[] = [];
  if (!p.libelle || p.libelle.trim().length < 2 || p.libelle.length > 120) e.push('Libellé obligatoire (2 à 120 caractères).');
  if (!p.programme || !PROGRAMMES_COMMANDE.includes(p.programme)) e.push('Programme inconnu.');
  if (p.unite !== null && p.unite !== undefined && (p.unite.trim() === '' || p.unite.length > 20)) e.push('Unité : 1 à 20 caractères ou vide (non renseignée).');
  if (!Number.isInteger(p.ordre) || (p.ordre as number) < 1 || (p.ordre as number) > 9999) e.push('Ordre d\'affichage : entier entre 1 et 9999.');
  if (!p.categorie || p.categorie.trim() === '') e.push('Catégorie obligatoire.');
  if (!p.regleCalcul || p.regleCalcul.trim() === '') e.push('Règle de calcul documentée obligatoire.');
  if (!Array.isArray(p.sources) || p.sources.length === 0) e.push('Au moins une source de données (produit du module Intrants) est obligatoire.');
  else for (const s of p.sources) {
    if (!s.nom || s.nom.trim() === '') e.push('Source : nom du produit obligatoire.');
    if (s.facteur !== null && (typeof s.facteur !== 'number' || !(s.facteur > 0) || s.facteur > 100_000)) e.push(`Source « ${s.nom} » : facteur > 0 attendu (ou vide = inconnu).`);
  }
  return { ok: e.length === 0, erreurs: e };
}

/** Un produit utilise-t-il une conversion encore inconnue ? */
export const conversionManquante = (p: Pick<ProduitCatalogue, 'sources'>): boolean => p.sources.some((s) => s.facteur === null);

// ---------------------------------------------------------------------------------------------------------------
// Adaptateurs : sorties de calculerModules() → MoisValeurs
// ---------------------------------------------------------------------------------------------------------------
export interface LigneStockModule {
  cle: string; nomPersonnalise?: string; nomMedicament?: string;
  debut?: number | null; recue?: number | null; utilisee?: number | null; redeployee?: number | null; perdue?: number | null; sortieAc?: number | null;
  joursRupture?: number | null; sdu?: number | null; debutEffectif?: number; restantFin?: number;
}
export interface LigneInventaireModule { id: string; nom: string; saisi?: boolean; calcul: { pu: number; debut: number; entree: number; perime: number; fin: number; dispensation: number | null; compte: boolean } }

/** Nom d'un produit de stock à partir de sa clé (« PALU:3 ») dans STOCK_PROGRAMMES, ou de son nom personnalisé. */
export function nomProduitStock(l: Pick<LigneStockModule, 'cle' | 'nomPersonnalise' | 'nomMedicament'>, produitsProgramme: readonly string[]): string | null {
  if (l.nomPersonnalise) return l.nomPersonnalise;
  if (l.nomMedicament) return l.nomMedicament;
  const m = /^[A-Z_]+:(\d+)$/.exec(l.cle);
  return m ? (produitsProgramme[Number(m[1])] ?? null) : null;
}

const nul = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const mult = (v: number | null, f: number): number | null => (v === null ? null : r4(v * f));

/** Un mois d'une ligne de stock (module Intrants), converti dans l'unité de commande par `facteur`. */
export function moisDepuisStock(m: MoisRef, l: LigneStockModule | undefined, facteur: number): MoisValeurs {
  if (!l) return moisVide(m);
  const aj = (nul(l.redeployee) ?? 0) + (nul(l.sortieAc) ?? 0);
  const aucun = nul(l.redeployee) === null && nul(l.sortieAc) === null;
  return {
    ...m, present: true,
    debut: mult(nul(l.debutEffectif) ?? nul(l.debut), facteur), recue: mult(nul(l.recue), facteur), utilisee: mult(nul(l.utilisee), facteur),
    perdue: mult(nul(l.perdue), facteur), ajustement: aucun ? null : mult(aj, facteur),
    fin: mult(nul(l.sdu) ?? nul(l.restantFin), facteur), joursRupture: nul(l.joursRupture),
  };
}

/** Additionne des présentations regroupées (ACT = ASAQ + AL) dans l'unité de commande. Un mois est présent si l'une des sources l'est. */
export function sommerMois(m: MoisRef, parts: readonly MoisValeurs[]): MoisValeurs {
  const presentes = parts.filter((p) => p.present);
  if (presentes.length === 0) return moisVide(m);
  const s = (k: 'debut' | 'recue' | 'utilisee' | 'perdue' | 'ajustement' | 'fin'): number | null => {
    const v = presentes.map((p) => p[k]).filter((x): x is number => x !== null);
    return v.length ? r4(v.reduce((a, b) => a + b, 0)) : null;
  };
  const jr = presentes.map((p) => p.joursRupture).filter((x): x is number => x !== null);
  return { ...m, present: true, debut: s('debut'), recue: s('recue'), utilisee: s('utilisee'), perdue: s('perdue'), ajustement: s('ajustement'), fin: s('fin'),
    // jours de rupture d'un regroupement : un seul jour de rupture du groupe entier compte (maximum), jamais la somme
    joursRupture: jr.length ? Math.max(...jr) : null };
}

/** Un mois d'inventaire FANOME. Les jours de rupture ne figurent pas dans l'inventaire : saisie manuelle. */
export function moisDepuisInventaire(m: MoisRef, l: LigneInventaireModule | undefined): MoisValeurs {
  if (!l || !l.calcul.compte) return moisVide(m);
  const c = l.calcul;
  return { ...m, present: true, debut: c.debut, recue: c.entree, utilisee: c.dispensation, perdue: c.perime, ajustement: 0, fin: c.fin, joursRupture: null };
}

export interface ParamsCommandeManuels { prixUnitaire?: number | null; livrees?: number | null; joursRupture?: number | null; observations?: string }

/** Construit la ligne de commande d'un produit du catalogue Paludisme/PF à partir des mois du module Intrants. */
export function entreeDepuisCatalogue(
  p: ProduitCatalogue, fenetre: readonly MoisRef[],
  stockParMois: ReadonlyArray<ReadonlyArray<LigneStockModule>>, produitsProgramme: readonly string[],
  actConsultationsParMois: ReadonlyArray<Partial<Record<TrancheAct, number>> | null> | null,
  manuel: ParamsCommandeManuels = {},
): EntreeLigneCommande {
  const manquante = conversionManquante(p);
  const mois = fenetre.map((m, i) => {
    const lignes = stockParMois[i] ?? [];
    const parts = p.sources.map((s) => moisDepuisStock(m, lignes.find((l) => nomProduitStock(l, produitsProgramme) === s.nom), s.facteur ?? 1));
    return sommerMois(m, parts);
  });
  let distribueesParMois: Array<number | null> | undefined;
  if (p.sourceUtilisee === 'CONSULTATIONS_ACT' && p.trancheAct) {
    distribueesParMois = fenetre.map((_, i) => { const c = actConsultationsParMois?.[i]; return c ? (c[p.trancheAct!] ?? 0) : null; });
    // un mois sans aucune consultation enregistrée n'a pas de cas : on ne l'invente pas
  }
  return {
    libelle: p.libelle, unite: p.unite, mois, distribueesParMois, conversionManquante: manquante,
    joursRuptureManuel: manuel.joursRupture ?? null, prixUnitaire: manuel.prixUnitaire ?? null, livrees: manuel.livrees ?? null, observations: manuel.observations ?? '',
  };
}


// ---------------------------------------------------------------------------------------------------------------
// Arrondi de la quantité à commander au conditionnement réel (réglage administrateur ; ne modifie JAMAIS M ni les formules)
// ---------------------------------------------------------------------------------------------------------------
/**
 * Quantité à commander arrondie VERS LE HAUT au multiple du conditionnement (boîte, plaquette, carton…), à partir de M (= L − I).
 * null si M n'est pas calculable, si M ≤ 0 (rien à commander) ou si aucun conditionnement valide n'est défini (aucun arrondi inventé).
 * Un conditionnement non entier ou ≤ 0 est ignoré. Une tolérance de 1e-9 évite qu'un M de 20,0000000001 saute au multiple suivant.
 */
export function arrondirAuConditionnement(m: number | null, conditionnement: number | null | undefined): number | null {
  if (m === null || !Number.isFinite(m) || m <= 0) return null;
  if (conditionnement === null || conditionnement === undefined || !Number.isInteger(conditionnement) || conditionnement < 1) return null;
  return Math.ceil(m / conditionnement - 1e-9) * conditionnement;
}
